/**
 * A dialog with its sections down the left, one of them open on the right.
 *
 * THE SHAPE FOR SOMETHING WITH PARTS, where `Dialog` is the shape for one form. A tool has an
 * action, a when, the caller's details, a request, a response and a test run, and a single
 * scrolling column of all of them read as a page to scroll past rather than a thing to set
 * up. Here the nav is the table of contents, always in view, and the pane shows one part
 * with its own title and a sentence on what it is for.
 *
 * SAME LAYER AS `Dialog`: the same scrim instead of a shadow, and the same escape hatches
 * through `useModalLayer`. The size is fixed rather than fitted to the content, so moving
 * between a short section and a long one does not make the panel jump under the pointer.
 *
 * NO HEADER BAND. The pane's title is the heading, and the close button sits in the corner
 * over it. The footer is the caller's, as in `Dialog`.
 *
 * Under 720px it becomes the screen, as a wide `Dialog` does, and the nav becomes a row of
 * chips above the pane.
 */

import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconButton, cx } from "../ui";
import { Close } from "./Icons";
import { useModalLayer } from "./useModalLayer";

export type NavSection<Id extends string> = { id: Id; label: string; icon: ReactNode };

export function SectionDialog<Id extends string>({
  label,
  navHead,
  sections,
  active,
  onSelect,
  title,
  intro,
  banner,
  footer,
  onClose,
  children,
}: {
  /** The dialog's accessible name. The pane's title names the section, not the dialog. */
  label: string;
  /** Above the nav, in view whichever section is open. */
  navHead?: ReactNode;
  sections: Array<NavSection<Id>>;
  active: Id;
  onSelect: (id: Id) => void;
  title: ReactNode;
  intro?: ReactNode;
  /** Above the title, whichever section is open: a refusal that applies to the whole thing. */
  banner?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const pane = useRef<HTMLDivElement>(null);

  useModalLayer(panel, onClose);

  // A new section starts at its top, not wherever the last one was scrolled to.
  useEffect(() => {
    pane.current?.scrollTo?.({ top: 0 });
  }, [active]);

  return createPortal(
    <div
      className={cx(
        "fixed inset-0 z-50 grid place-items-center p-4 max-[720px]:p-0",
        "bg-[color-mix(in_srgb,var(--color-ink)_38%,transparent)]",
      )}
      // `mousedown`, as in `Dialog`: a text selection released past the edge is not a close.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={cx(
          "relative flex h-[min(90vh,820px)] w-[min(96vw,1120px)] flex-col overflow-hidden rounded-lg border border-line bg-paper outline-none",
          "max-[720px]:h-[100dvh] max-[720px]:w-full max-[720px]:rounded-none max-[720px]:border-0",
        )}
      >
        <div className="grid min-h-0 flex-1 grid-cols-[260px_minmax(0,1fr)] max-[720px]:grid-cols-1 max-[720px]:grid-rows-[auto_minmax(0,1fr)]">
          <div className="flex min-h-0 flex-col overflow-y-auto border-r border-line p-3 max-[720px]:overflow-visible max-[720px]:border-r-0 max-[720px]:border-b">
            {navHead && <div className="mb-3 border-b border-line-soft px-1 pt-1 pb-3 max-[720px]:mr-11">{navHead}</div>}
            <nav aria-label="Sections" className="flex flex-col gap-1 max-[720px]:-mx-1 max-[720px]:flex-row max-[720px]:overflow-x-auto max-[720px]:px-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  aria-current={section.id === active ? "page" : undefined}
                  onClick={() => onSelect(section.id)}
                  className={cx(
                    "flex h-11 flex-none items-center gap-3 rounded-md px-3 text-left text-base",
                    "max-[720px]:h-9 max-[720px]:gap-2 max-[720px]:text-sm",
                    section.id === active ? "bg-sunken font-medium text-ink" : "text-ink hover:bg-line-soft",
                  )}
                >
                  <span className="flex-none">{section.icon}</span>
                  {section.label}
                </button>
              ))}
            </nav>
          </div>

          <div ref={pane} className="min-h-0 overflow-y-auto">
            <div className="px-10 pt-9 pb-12 max-[720px]:px-4 max-[720px]:pt-5">
              {banner}
              <h2 className="pr-10 text-2xl font-semibold tracking-[-0.01em] text-ink max-[720px]:text-xl">
                {title}
              </h2>
              {intro && <p className="mt-2 max-w-[680px] text-base text-ink">{intro}</p>}
              <div className="mt-7">{children}</div>
            </div>
          </div>
        </div>

        <IconButton type="button" className="absolute top-5 right-5 max-[720px]:top-3 max-[720px]:right-3" onClick={onClose}>
          <Close size={18} />
          <span className="sr-only">Close</span>
        </IconButton>

        {footer && <div className="flex flex-none flex-wrap items-center gap-2 border-t border-line px-6 py-4 max-[720px]:px-4">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
